<template>
  <CDialog
    :visible.sync="visible"
    :title="$t('Extra information')"
    :width="dialogWidth"
    @open="open"
    @close="close"
  >
    <Loading :loading="loading" v-if="loading" />

    <div v-else>
      <div class="selector-group">
        <CSelector
          :label="$t('Monthly usage report by product.')"
          v-model="month"
          :options="monthList"
          :use-input="false"
          width="240px"
          @change="changeSelector"
        />

        <div class="download-button-group" v-if="hasDownloadFeature">
          <CButton
            icon="get_app"
            round
            flat
            :disable="!isDownloadable"
            :color="isDownloadable || loading ? 'primary' : 'muted'"
            :loading="loading"
            :tooltip="$t('Download the adjustments (.csv)')"
            @click="handleDownloadCsv"
          />
        </div>
      </div>

      <div class="description" v-if="vendor !== 'azure'">
        <p v-if="$display('usage_account_menu_fees_fee')">
          <span class="label">Fee:</span>
          {{
            $t('Any upfront annual fee that you paid like RI upfront fee, Marketplace fee.. etc')
          }}
        </p>

        <p v-if="$display('usage_account_menu_fees_credit')">
          <span class="label">Credit:</span>
          {{ $t("Any credits that AWS applied to user's bill.") }}
        </p>

        <p v-if="$display('usage_account_menu_fees_refund')">
          <span class="label">Refund:</span>
          {{ $t('Negative charges that AWS refunded money to user for.') }}
        </p>

        <p v-if="$display('usage_account_menu_fees_other_fees')">
          <span class="label">Other Fees:</span>
          {{ $t('Fees other than Fee/Credit/Refund.') }}
        </p>
      </div>
      
      <div class="fees-table">
        <InfoWall loading v-if="loading" height="300" />

        <el-tabs v-model="activeTab" v-else>
          <el-tab-pane
            :label="$t(data.name)"
            :name="data.name"
            lazy
            v-for="data in feesData"
            :key="data.name"
          >
            <FeesTable :type="data.name" :data="data.data" :req-hash="reqHash" />
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
  </CDialog>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { monthArray } from '@/lib/formattingData';
import { CalcDialogSize } from '@/lib/tools';
import CDialog from '@/components/uikit/CDialog';
import CButton from '@/components/uikit/CButton';
import CSelector from '@/components/uikit/CSelector';
import SectionHeader from '@/components/uikit/SectionHeader';
import FeesTable from '@/pages/UsageReport/DialogExtraInfoFeesTable';
import InfoWall from '@/components/uikit/InfoWall';
import Loading from '@/components/uikit/LoadingMask';

import { detectAzureFeeItemType } from '@/vuex/modules/accounts';
import { saveAs } from 'file-saver';
import { basic } from '@/lib/dates';

const messages = {
  en: {
    'Extra information': 'Adjustments',
    'Monthly usage report by product.': 'Month',
    'You can check the fee for subscriptions payment, AWS credit, refund.':
      'You can check the fee for subscriptions payment, AWS credit, refund.',
    'Any upfront annual fee that you paid like RI upfront fee, Marketplace fee.. etc':
      'Any upfront annual or monthly fee that you paid (RI upfront cost, marketplace charges, etc.)',
    "Any credits that AWS applied to user's bill.": 'Any credits that were applied to your bill',
    'Negative charges that AWS refunded money to user for.':
      'Negative charges applied on usage after it has been paid for',
    'Fees other than Fee/Credit/Refund.': 'Fees other than Fee/Credit/Refund.',
    Fee: 'Fees',
    Credit: 'Credits',
    Refund: 'Refunds',
    OtherFee: 'Other',
  },
  ja: {
    'Extra information': 'その他の明細情報',
    'Monthly usage report by product.': '月別およびサービス別の利用レポート',
    'You can check the fee for subscriptions payment, AWS credit, refund.':
      'サブスクリプション支払い額、AWSクレジット、返金の情報を確認できます。',
    'Any upfront annual fee that you paid like RI upfront fee, Marketplace fee.. etc':
      '「全前払いRI」や「一部前払いRI」などの前払い年間料金。',
    "Any credits that AWS applied to user's bill.": 'AWS が請求に適用したすべてのクレジット。',
    'Negative charges that AWS refunded money to user for.':
      'AWS から負の課金として払い戻される金額。',
    'Fees other than Fee/Credit/Refund.': 'Fee/Credit/Refund以外の一時金。',
  },
};

export default {
  name: 'DialogExtraInfo',
  i18n: { messages },
  components: {
    CButton,
    CDialog,
    CSelector,
    SectionHeader,
    FeesTable,
    InfoWall,
    Loading,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 'Fee',
      dialogWidth: null,
      month: '',
    };
  },
  watch: {
    month(month) {
      if (this.vendor === 'azure') {
        this.activeTab = 'Software License';
      } else {
        this.activeTab = 'Fee';
      }
      this.fetchFees(month);
    },
  },
  created() {
    this.dialogWidth = CalcDialogSize(720);

    // Set Active tab
    if (this.vendor === 'azure') {
      this.activeTab = 'Software License';
    } else {
      if (this.userMeta['usage_account_menu_fees_fee']) {
        this.activeTab = 'Fee';
      } else if (this.userMeta['usage_account_menu_fees_credit']) {
        this.activeTab = 'Credit';
      } else if (this.userMeta['usage_account_menu_fees_refund']) {
        this.activeTab = 'Refund';
      } else if (this.userMeta['usage_account_menu_fees_other_fees']) {
        this.activeTab = 'OtherFees';
      }
    }
  },
  computed: {
    ...mapState({
      dataReady: state => state.userState.dataReady.report,
      vendor: state => state.vendor,
    }),

    ...mapState('user', {
      userMeta: state => state.data.meta,
    }),

    ...mapState('accounts', {
      fees: state => state.fees,
      originalAzureFees: state => state.originalAzureFees,
      feesRequests: state => state.feesRequests,
      selectedId: state => state.selectedId,
    }),

    ...mapGetters('accounts', {
      getAccounts: 'getAccounts',
    }),

    hasDownloadFeature() {
      return this.vendor === 'azure';
    },

    /**
     * Return fees data
     * @return {array} -  [{ name, label, data, display }]
     */
    feesData() {
      if (this.vendor === 'azure') {
        return this.azureFeesData;
      }
      const fees = this.fees[this.reqHash];

      if (!fees) return [];

      let arr = [];

      const otherFee = {
        name: 'OtherFee',
        label: this.$t('Other Fees'),
        data: [],
        display: this.userMeta['usage_account_menu_fees_other_fees'],
      };

      Object.keys(fees).forEach(key => {
        if (key === 'id' || key === 'name') return false;

        if (key === 'Fee') {
          arr.splice(0, 0, {
            name: key,
            label: key,
            data: fees[key],
            display: this.userMeta['usage_account_menu_fees_fee'],
          });
        } else if (key === 'Credit') {
          arr.splice(1, 0, {
            name: key,
            label: key,
            data: fees[key],
            display: this.userMeta['usage_account_menu_fees_credit'],
          });
        } else if (key === 'Refund') {
          arr.splice(2, 0, {
            name: key,
            label: key,
            data: fees[key],
            display: this.userMeta['usage_account_menu_fees_refund'],
          });
        } else {
          fees[key].forEach(i => {
            otherFee.data.push(i);
          });
        }
      });

      arr.splice(4, 0, otherFee);

      return arr.filter(i => i.display);
    },

    azureFeesData() {
      const fees = this.fees[this.reqHash];
      if (!fees) return [];

      let arr = [];
      const otherFee = {
        name: 'OtherFee',
        label: this.$t('Other Fees'),
        data: [],
        display: true,
      };

      Object.keys(fees).forEach(key => {
        if (key === 'id' || key === 'name') return false;

        if (key === 'software_license') {
          arr.splice(0, 0, {
            name: 'Software License',
            label: 'Software License',
            data: fees[key],
            display: true,
          });
        } else if (key === 'marketplace') {
          arr.splice(1, 0, {
            name: 'Marketplace',
            label: 'Marketplace',
            data: fees[key],
            display: true,
          });
        } else if (key === 'ri') {
          arr.splice(2, 0, {
            name: 'RI',
            label: 'RI',
            data: fees[key],
            display: true,
          });
        } else {
          fees[key].forEach(i => {
            otherFee.data.push(i);
          });
        }
      });

      arr.splice(3, 0, otherFee);

      return arr.filter(i => i.display);
    },

    reqHash() {
      return `${this.selectedId}-${this.month}`;
    },

    loading() {
      const request = this.feesRequests[this.reqHash];
      if (!request) {
        return true;
      }

      return request.inProgress;
    },

    visible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    monthList() {
      return monthArray(this.dataReady, 12);
    },

    isDownloadable() {
      const fees = this.fees[this.reqHash];
      if (fees) {
        return true;
      }
      return false;
    },
  },
  methods: {
    open() {
      this.fetchFees(this.month);
    },
    close() {
      return this.$store.dispatch('accounts/removeFeeRequest', this.reqHash);
    },

    changeSelector(value) {
      this.month = value;
    },

    fetchFees() {
      return this.$store.dispatch('accounts/fetchFees', this.month);
    },

    handleDownloadCsv() {
      const fees = this.originalAzureFees[this.reqHash];
      if (!fees) return;

      // Create account map to use later.
      const accounts = this.getAccounts;
      const accountMap = {};
      for (const account of accounts) {
        accountMap[account.account_id] = account;
      }

      // Generate the CSV data.
      const fileName = `adjustments-${this.reqHash}.csv`;
      const csvHeader = [
        'CustomerID',
        'CustomerName',
        'DomainName',
        'StartDate',
        'EndDate',
        'UsageDate',
        'ChargeType',
        'FeeType',
        'ProductName',
        'UnitPrice',
        'Quantity',
        'BaseCurrency',
        'TargetCurrency',
        'ExchangeRate',
        'Cost',
      ];
      const csvData = [];
      for (const item of fees) {
        // Get account name from account Id.
        const account = accountMap[item.account];
        let accountName = item.account;
        if (account && account.account_name) {
          accountName = account.account_name;
        }

        // Get fee type.
        const feeType = detectAzureFeeItemType(item);
        let feeTypeName = 'Other';
        switch (feeType) {
          case 'marketplace':
            feeTypeName = 'Marketplace';
            break;
          case 'ri':
            feeTypeName = 'RI';
            break;
          case 'software_license':
            feeTypeName = 'Software License';
            break;
        }

        // Get start date and end date.
        let startDate = '';
        let endDate = '';
        if (item.timeInterval) {
          const interval = item.timeInterval.split('/');
          startDate = basic(interval[0]);
          endDate = basic(interval[1]);
        }

        // Get usage date.
        let usageDate = '';
        if (item.date) {
          usageDate = item.date.replaceAll('-', '/');
        }

        // Add record to CSV data.
        csvData.push([
          item.account,
          accountName,
          item.domainName,
          startDate,
          endDate,
          usageDate,
          item.chargeType,
          feeTypeName,
          item.productName,
          item.effectiveUnitPrice,
          item.billableQuantity,
          item.baseCurrency,
          item.targetCurrency,
          item.exchangeRate,
          item.targetCost,
        ]);
      }

      // Convert CSV data to String.
      var csvText = '';
      csvText += this.objectToCSVRow(csvHeader);
      for (const item of csvData) {
        csvText += this.objectToCSVRow(item);
      }

      // Save file to local machine.
      var blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, fileName, { autoBom: true });
    },

    objectToCSVRow(dataObject) {
      var dataArray = new Array();
      for (var key in dataObject) {
        var innerValue = dataObject[key] ? dataObject[key].toString() : '';
        var result = innerValue.replace(/"/g, '""');
        result = '"' + result + '"';
        dataArray.push(result);
      }
      return dataArray.join(',') + '\r\n';
    },
  },
};
</script>

<style lang="scss" scoped>
.description {
  margin-bottom: 1rem;

  p {
    margin-bottom: 0.3rem;
  }
  .label {
    font-weight: 700;
    margin-right: 0.5rem;
  }
}

.selector-group {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: $margin-xl;

  .download-button-group {
    margin-top: $margin-xs;
    margin-left: $margin-sm;
  }
}
</style>
